import { DateFormats, formatDate, sortAsc } from '@strata/helper-functions';
import { SuggestSingleSelect } from '@strata/react-selects';
import { ClinicalPolicyStatusIndicator } from '@strata/react-status-indicators';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { List } from 'components/List';
import { CreateDuplicatePolicyDialog } from 'pages/clinicalPolicy/dialogs/CreateDuplicatePolicyDialog';
import { handleError } from 'services/ErrorManagement';
import { get, useGet } from 'services/Http';

const columns = [
    'Id',
    'Product',
    'Diagnoses',
    'Status',
    'Effective Date',
    'End Date',
    'Assigned To',
    'Last Updated On',
    'Updated By',
];
const columnWidths = [0, 180, 240, 170, 110, 110, 180, 160, 200];

export const PolicyList = () => {
    const { payerId } = useParams();
    const navigate = useNavigate();

    const [showCreatePolicyDialog, setShowCreatePolicyDialog] = useState(false);

    const payers = useGet('/reference/payers/');
    const payerOptions = useMemo(
        () => payers?.map((payer) => ({ name: payer.payer_id })),
        [payers],
    );

    const [policies, setPolicies] = useState();
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setPolicies();

        void (async () => {
            if (payerId) {
                try {
                    const [activePolicies, retiredPolicies] = await Promise.all([
                        get(`/clinicalpolicy/admin/${payerId}/drafts`),
                        get(`/clinicalpolicy/admin/${payerId}/drafts?retired=true`),
                    ]);

                    if (activePolicies && retiredPolicies) {
                        setPolicies(
                            [...activePolicies, ...retiredPolicies]
                                .map((policy) => ({
                                    ...policy,
                                    policy_status: policy.policy_status?.trim(),
                                    diagnosis_oids:
                                        policy.diagnosis_oids?.sort(sortAsc).join(', ') ?? '',
                                }))
                                .sort((a, b) =>
                                    sortAsc(
                                        `${a.product_name}${a.diagnosis_oids}${dayjs(
                                            b.status_date,
                                        ).valueOf()}`,
                                        `${b.product_name}${b.diagnosis_oids}${dayjs(
                                            a.status_date,
                                        ).valueOf()}`,
                                    ),
                                )
                                .map((policy) => [
                                    policy.draft_id,
                                    policy.product_name,
                                    policy.diagnosis_oids,
                                    ClinicalPolicyStatusIndicator.StatusMessages[
                                        policy.policy_status
                                    ],
                                    formatDate(policy.effective_date),
                                    formatDate(policy.end_date),
                                    policy.assigned_to,
                                    formatDate(policy.status_date, DateFormats.DATE_TIME),
                                    policy.username,
                                ]),
                        );
                    }
                } catch (err) {
                    handleError('Error fetching policy data', err);
                }
            }
        })();
    }, [payerId]);

    return (
        <div className="sds_list-page">
            <div className="sds_list-page__content--top">
                <SuggestSingleSelect
                    id="payerId"
                    name="payerId"
                    label="Payer"
                    isLoading={!payers}
                    values={payerOptions}
                    helperText="Payer to view the list for"
                    onChange={(newValue) => navigate(`/policy-list/${newValue?.name ?? ''}`)}
                    selectedItem={payerOptions?.find((payer) => payer.name === payerId)}
                />
            </div>
            <CreateDuplicatePolicyDialog
                isOpen={showCreatePolicyDialog}
                onSecondaryButtonClick={() => setShowCreatePolicyDialog(false)}
                payerId={payerId}
            />
            {payerId && (
                <List
                    pageTitle="Clinical Policies"
                    columnNames={columns}
                    columnWidths={columnWidths}
                    isLoading={!policies}
                    listData={policies}
                    editPath={`/policy-edit/${payerId}`}
                    createButtonLabel="Create Clinical Policy"
                    onCreateButtonClick={() => setShowCreatePolicyDialog(true)}
                    rowClassName={(row) =>
                        row[3] ===
                        ClinicalPolicyStatusIndicator.StatusMessages[
                            ClinicalPolicyStatusIndicator.Statuses.IN_PROD
                        ]
                            ? 'sds_list-page__row--in-prod'
                            : undefined
                    }
                />
            )}
        </div>
    );
};
